// Тонкий fetch-клиент. Прокидывает X-Telegram-Init-Data в каждый запрос.

import { getInitData } from '../lib/telegram';

// VITE_API_URL — базовый URL Worker'а (https://...workers.dev). Прописывается в .env
// при build-е Mini App. Если пусто — fallback на same-origin (для локалки, где
// Mini App и API под Vite proxy на одном порту).
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/**
 * Ошибка запроса к API с разобранными полями.
 *
 * Раньше бросалась обычная Error со строкой вида
 * `POST /api/... → 500 Internal Server Error: {...}` — и она же попадала
 * пользователю на экран целиком. Теперь техническая часть живёт в полях
 * (для логов и разбора), а человеку показывается текст из lib/errors.ts,
 * выбранный по `status`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly method: string;
  readonly path: string;
  readonly body: unknown;

  constructor(args: {
    status: number;
    statusText: string;
    method: string;
    path: string;
    body: unknown;
  }) {
    super(`${args.method} ${args.path} → ${args.status} ${args.statusText}`);
    this.name = 'ApiError';
    this.status = args.status;
    this.method = args.method;
    this.path = args.path;
    this.body = args.body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Telegram-Init-Data': getInitData(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError({ status: res.status, statusText: res.statusText, method, path, body });
  }
  return res.json() as Promise<T>;
}

import type {
  AnomaliesData,
  BudgetResponse,
  BudgetSuggestResponse,
  ByCategoryAnalytics,
  CalendarHeatmap,
  Category,
  CategoryTrends,
  DayOfWeekData,
  Debt,
  DebtDeep,
  DebtStrategiesResponse,
  DebtWhatIfResponse,
  HealthScore,
  HealthSummary,
  InsightsResponse,
  Kpi,
  LimitsResponse,
  Projection,
  RecentTransaction,
  RecurringResponse,
  RunRate,
  SankeyResponse,
  Scenario,
  SpendingMode,
  StreakData,
  TimeseriesAnalytics,
  TopMerchantsResponse,
  Transaction,
  TransactionFilters,
  WeeklyAnalytics,
} from './types';

export type DebtMutationResponse = {
  debt: Debt | null;
  new_balance_minor: string;
  closed: boolean;
  // true — сервер увидел повтор с тем же idempotency-ключом и ничего не записал.
  deduped?: boolean;
};

export type BatchPaymentItem = {
  debt_id: string;
  amount_minor: string;
  is_prepayment: boolean;
  period_ym?: string | null;
};

export type FxCurrent = {
  base: 'UZS';
  rates: { USD: number | null; EUR: number | null; RUB: number | null };
  as_of: string;
};

export type BatchPaymentResultItem = {
  debt_id: string;
  ok: boolean;
  new_balance_minor?: string;
  closed?: boolean;
  deduped?: boolean;
  error?: string;
};

export type CreateTransactionInput = {
  kind: 'expense' | 'income';
  amount_minor: string;
  currency: string;
  category_slug?: string | null;
  note?: string | null;
  occurred_at?: string;
};

export type UpdateTransactionInput = Partial<CreateTransactionInput>;

export type CreateCategoryInput = {
  name: string;
  kind: 'expense' | 'income';
  emoji?: string;
  color?: string;
};

export type UpdateCategoryInput = {
  name?: string;
  emoji?: string | null;
  color?: string | null;
  spending_mode?: SpendingMode;
};

function buildQuery(filters?: TransactionFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.period) params.set('period', filters.period);
  if (filters.from_date) params.set('from_date', filters.from_date);
  if (filters.to_date) params.set('to_date', filters.to_date);
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.category) params.set('category', filters.category);
  if (filters.q) params.set('q', filters.q);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const api = {
  kpi: () => request<Kpi>('/api/kpi'),
  // include_closed=1 — иначе закрытые долги вообще не доезжают до клиента и
  // блок «Закрытые» на экране всегда пустой.
  debts: () => request<Debt[]>('/api/debts?include_closed=1'),

  transactions: (filters?: TransactionFilters | number) => {
    // Обратная совместимость: api.transactions(50) принимался как limit-аргумент.
    const f = typeof filters === 'number' ? { limit: filters } : filters;
    return request<Transaction[]>(`/api/transactions${buildQuery(f)}`);
  },
  transaction: (id: string) => request<Transaction>(`/api/transactions/${id}`),
  createTransaction: (input: CreateTransactionInput) =>
    request<Transaction>('/api/transactions', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateTransaction: (id: string, input: UpdateTransactionInput) =>
    request<Transaction>(`/api/transactions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteTransaction: (id: string) =>
    request<{ ok: true }>(`/api/transactions/${id}`, { method: 'DELETE' }),

  categories: (kind?: 'expense' | 'income', includeHidden = false) => {
    const params = new URLSearchParams();
    if (kind) params.set('kind', kind);
    if (includeHidden) params.set('include_hidden', '1');
    const q = params.toString();
    return request<Category[]>(`/api/categories${q ? `?${q}` : ''}`);
  },
  createCategory: (input: CreateCategoryInput) =>
    request<Category>('/api/categories', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateCategory: (id: string, input: UpdateCategoryInput) =>
    request<Category>(`/api/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  setCategoryHidden: (id: string, isHidden: boolean) =>
    request<Category>(`/api/categories/${id}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ is_hidden: isHidden }),
    }),

  weeklyAnalytics: () => request<WeeklyAnalytics>('/api/analytics/weekly'),
  recentTransactions: (limit = 5) =>
    request<RecentTransaction[]>(`/api/analytics/recent?limit=${limit}`),
  byCategoryAnalytics: (
    period: 'week' | 'month' | 'year' = 'month',
    kind: 'expense' | 'income' = 'expense',
  ) => request<ByCategoryAnalytics>(`/api/analytics/by-category?period=${period}&kind=${kind}`),
  timeseriesAnalytics: (period: 'week' | 'month' | 'year' = 'month') =>
    request<TimeseriesAnalytics>(`/api/analytics/timeseries?period=${period}`),

  // Глубокая аналитика
  healthScore: () => request<HealthScore>('/api/analytics/health-score'),
  categoryTrends: (kind: 'expense' | 'income' = 'expense', monthsBack = 6) =>
    request<CategoryTrends>(
      `/api/analytics/category-trends?kind=${kind}&months_back=${monthsBack}`,
    ),
  calendarHeatmap: (months = 3) =>
    request<CalendarHeatmap>(`/api/analytics/calendar-heatmap?months=${months}`),
  dayOfWeek: (days = 90) => request<DayOfWeekData>(`/api/analytics/day-of-week?days=${days}`),
  streak: () => request<StreakData>('/api/analytics/streak'),
  anomalies: (days = 30, kind: 'expense' | 'income' = 'expense', z = 2) =>
    request<AnomaliesData>(`/api/analytics/anomalies?days=${days}&kind=${kind}&z=${z}`),
  insights: () => request<InsightsResponse>('/api/analytics/insights'),
  runRate: () => request<RunRate>('/api/analytics/run-rate'),
  debtDeep: () => request<DebtDeep>('/api/analytics/debt-deep'),
  debtStrategies: (prepayMinor?: bigint) => {
    const q = prepayMinor != null ? `?prepay_minor=${prepayMinor.toString()}` : '';
    return request<DebtStrategiesResponse>(`/api/analytics/debt-strategies${q}`);
  },
  debtWhatIf: (
    extraMinor: bigint,
    strategy: 'snowball' | 'avalanche' = 'avalanche',
    baseMinor?: bigint,
  ) => {
    const params = new URLSearchParams();
    params.set('extra_minor', extraMinor.toString());
    params.set('strategy', strategy);
    if (baseMinor != null) params.set('base_minor', baseMinor.toString());
    return request<DebtWhatIfResponse>(`/api/analytics/debt-whatif?${params.toString()}`);
  },

  // Round 2
  topMerchants: (
    period: 'week' | 'month' | 'year' = 'month',
    kind: 'expense' | 'income' = 'expense',
    limit = 10,
  ) =>
    request<TopMerchantsResponse>(
      `/api/analytics/top-merchants?period=${period}&kind=${kind}&limit=${limit}`,
    ),
  recurring: () => request<RecurringResponse>('/api/analytics/recurring'),
  sankey: (period: 'week' | 'month' | 'year' = 'month') =>
    request<SankeyResponse>(`/api/analytics/sankey?period=${period}`),

  limits: () => request<LimitsResponse>('/api/limits'),
  upsertLimit: (input: {
    category_id: string;
    monthly_amount_minor: string;
    currency?: string;
    note?: string | null;
  }) =>
    request<{ id: string; category_id: string; monthly_amount_minor: string }>('/api/limits', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteLimit: (id: string) => request<{ ok: true }>(`/api/limits/${id}`, { method: 'DELETE' }),

  // ===== Месячный бюджет =====
  budget: (period?: string) =>
    request<BudgetResponse>(`/api/budget${period ? `?period=${period}` : ''}`),
  budgetSuggest: (period: string) =>
    request<BudgetSuggestResponse>(`/api/budget/suggest?period=${period}`),
  upsertBudgetPlan: (input: {
    period: string;
    category_id: string;
    planned_amount_minor: string;
    note?: string | null;
  }) =>
    request<{ id: string; period: string; planned_amount_minor: string }>('/api/budget/plan', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  deleteBudgetPlan: (id: string) =>
    request<{ ok: true }>(`/api/budget/plan/${id}`, { method: 'DELETE' }),
  createBudgetPayment: (input: {
    period: string;
    label: string;
    category_id?: string | null;
    amount_minor: string;
    due_day?: number | null;
    is_recurring?: boolean;
    note?: string | null;
  }) =>
    request<{ id: string }>('/api/budget/payments', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateBudgetPayment: (
    id: string,
    patch: {
      label?: string;
      category_id?: string | null;
      amount_minor?: string;
      due_day?: number | null;
      is_recurring?: boolean;
      note?: string | null;
    },
  ) =>
    request<{ ok: true }>(`/api/budget/payments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  // Оплата планового платежа: сервер записывает расход и помечает строку
  // выполненной одним запросом. Снятие отметки мягко удаляет ту же транзакцию.
  // amountMinor не задан — списывается плановая сумма.
  setBudgetPaymentPaid: (
    id: string,
    paid: boolean,
    args?: { amountMinor?: bigint; occurredAt?: string },
  ) =>
    request<{
      ok: true;
      paid_at: string | null;
      transaction_id?: string | null;
      amount_minor?: string;
      deduped?: boolean;
    }>(`/api/budget/payments/${id}/paid`, {
      method: 'POST',
      body: JSON.stringify({
        paid,
        ...(args?.amountMinor != null ? { amount_minor: args.amountMinor.toString() } : {}),
        ...(args?.occurredAt ? { occurred_at: args.occurredAt } : {}),
      }),
    }),
  deleteBudgetPayment: (id: string) =>
    request<{ ok: true }>(`/api/budget/payments/${id}`, { method: 'DELETE' }),
  fillBudgetFromTemplate: (period: string) =>
    request<{ ok: true; plans: number; skipped: number }>('/api/budget/from-template', {
      method: 'POST',
      body: JSON.stringify({ period }),
    }),
  copyBudget: (from: string, to: string) =>
    request<{ ok: true; plans: number; payments: number }>('/api/budget/copy', {
      method: 'POST',
      body: JSON.stringify({ from, to }),
    }),

  scenarios: () => request<Scenario[]>('/api/scenarios'),
  projection: (scenarioId: string) =>
    request<Projection>(`/api/scenarios/${scenarioId}/projection`),
  createDebt: (input: {
    kind: 'bank' | 'uzum' | 'uzum_zero' | 'installment' | 'overdue' | 'company' | 'personal';
    title: string;
    lender?: string;
    principal_minor: string;
    monthly_payment_minor?: string;
    nominal_rate?: number;
    payments_left?: number;
    // YYYY-MM-DD. Срок, к которому долг нужно закрыть целиком.
    due_date?: string;
    note?: string;
    currency?: string;
  }) =>
    request<Debt>('/api/debts', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  lenders: () => request<{ lenders: string[] }>('/api/debts/lenders'),
  payDebt: (
    debtId: string,
    args: {
      amountMinor: bigint;
      isPrepayment: boolean;
      periodYm?: string | null;
      occurredAt?: string | null;
    },
    idempotencyKey?: string,
  ) =>
    request<DebtMutationResponse>(`/api/debts/${debtId}/payment`, {
      method: 'POST',
      headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : undefined,
      body: JSON.stringify({
        amount_minor: args.amountMinor.toString(),
        is_prepayment: args.isPrepayment,
        period_ym: args.periodYm ?? null,
        occurred_at: args.occurredAt ?? null,
      }),
    }),
  updateDebt: (debtId: string, patch: Record<string, unknown>) =>
    request<Debt>(`/api/debts/${debtId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  closeDebt: (debtId: string, idempotencyKey?: string) =>
    request<DebtMutationResponse>(`/api/debts/${debtId}/close`, {
      method: 'POST',
      headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : undefined,
      body: JSON.stringify({}),
    }),
  healthSummary: () => request<HealthSummary>('/api/health/summary'),

  correctDebt: (debtId: string, newBalanceMinor: bigint, note?: string, idempotencyKey?: string) =>
    request<DebtMutationResponse>(`/api/debts/${debtId}/correct`, {
      method: 'POST',
      headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : undefined,
      body: JSON.stringify({
        new_balance_minor: newBalanceMinor.toString(),
        note,
      }),
    }),

  fxCurrent: () => request<FxCurrent>('/api/fx/current'),

  payMonthlyBatch: (payments: BatchPaymentItem[], idempotencyKey?: string) =>
    request<{ results: BatchPaymentResultItem[] }>(`/api/debts/pay-monthly`, {
      method: 'POST',
      headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : undefined,
      body: JSON.stringify({ payments }),
    }),
};
